import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupportConversationDTO } from '@klaro7/shared-types';

import * as svc from '../services/support.service';

export function useMyThread() {
  return useQuery({
    queryKey: ['support-my'],
    queryFn: svc.getMyThread,
    staleTime: 1000 * 30,
  });
}

export function useInbox(status?: string) {
  return useQuery({
    queryKey: ['support-inbox', status ?? 'all'],
    queryFn: () => svc.listInbox(status),
    staleTime: 1000 * 15,
  });
}

export function useSupportMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['support-messages', conversationId],
    queryFn: () => svc.getMessages(conversationId!),
    enabled: !!conversationId,
    staleTime: 1000 * 10,
  });
}

export function useSendSupportMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content, files }: { conversationId: string; content: string; files?: File[] }) =>
      svc.sendMessage(conversationId, content, files ?? []),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['support-messages', v.conversationId] });
      qc.invalidateQueries({ queryKey: ['support-inbox'] });
      qc.invalidateQueries({ queryKey: ['support-my'] });
    },
  });
}

export function useBroadcastSupport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: svc.BroadcastPayload) => svc.broadcastMessage(payload),
    onSuccess: () => {
      // Every targeted thread bumped its lastMessage / unread — refresh the inbox
      // and the sidebar badge.
      qc.invalidateQueries({ queryKey: ['support-inbox'] });
      qc.invalidateQueries({ queryKey: ['support-unread-count'] });
    },
  });
}

export function useMarkSupportRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => svc.markRead(conversationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-inbox'] });
      qc.invalidateQueries({ queryKey: ['support-my'] });
      qc.invalidateQueries({ queryKey: ['support-unread-count'] });
    },
  });
}

export function useSupportStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      status,
      assignedAdminId,
    }: {
      conversationId: string;
      status: string;
      assignedAdminId?: string | null;
    }) => svc.setStatus(conversationId, status, assignedAdminId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-inbox'] }),
  });
}

export function useSupportAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      assignedAdminId,
    }: {
      conversationId: string;
      assignedAdminId: string | null;
    }) => svc.assign(conversationId, assignedAdminId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-inbox'] }),
  });
}

export function useOpenThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (proId: string) => svc.openThread(proId),
    onSuccess: (conv) => {
      // Optimistically insert into every cached inbox list so the thread pane can
      // resolve `active` immediately (the invalidate-driven refetch then replaces
      // it with the fully-populated row).
      qc.setQueriesData<SupportConversationDTO[]>({ queryKey: ['support-inbox'] }, (old) => {
        if (!old) return old;
        if (old.some((c) => c.id === conv.id)) return old;
        return [conv, ...old];
      });
      qc.invalidateQueries({ queryKey: ['support-inbox'] });
    },
  });
}
